import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import {
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  respondToNewPasswordChallenge,
  refreshTokens,
  saveTokens,
  getStoredTokens,
  clearStoredTokens,
  isTokenExpired,
  setAuthCookie,
  clearAuthCookie,
  type AuthTokens,
  type CognitoError,
} from '../cognito-auth'
import { setAccessTokenGetter } from '../api-client'

interface User {
  sub: string
  email?: string
}

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  accessToken: string | null
  user: User | null
}

// Decode JWT payload (doesn't verify signature, just extracts claims)
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

interface NewPasswordChallenge {
  email: string
  session: string
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string; needsNewPassword?: boolean }>
  setNewPassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>
  signOut: () => Promise<void>
  getAccessToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function extractUserFromTokens(accessToken: string, idToken?: string): User | null {
  const accessPayload = decodeJwtPayload(accessToken)
  if (!accessPayload || typeof accessPayload.sub !== 'string') return null

  let email: string | undefined
  if (idToken) {
    const idPayload = decodeJwtPayload(idToken)
    email = typeof idPayload?.email === 'string' ? idPayload.email : undefined
  }

  return {
    sub: accessPayload.sub,
    email,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    accessToken: null,
    user: null,
  })
  const [newPasswordChallenge, setNewPasswordChallenge] = useState<NewPasswordChallenge | null>(null)

  // Check for existing tokens on mount
  useEffect(() => {
    const checkAuth = async () => {
      const tokens = getStoredTokens()

      if (!tokens) {
        clearAuthCookie()
        setState({ isAuthenticated: false, isLoading: false, accessToken: null, user: null })
        return
      }

      // If token is expired, try to refresh
      if (isTokenExpired(tokens.expiresAt)) {
        try {
          const newTokens = await refreshTokens(tokens.refreshToken)
          const fullTokens: AuthTokens = {
            ...newTokens,
            refreshToken: tokens.refreshToken,
          }
          saveTokens(fullTokens)
          setAuthCookie()
          setState({
            isAuthenticated: true,
            isLoading: false,
            accessToken: newTokens.accessToken,
            user: extractUserFromTokens(newTokens.accessToken, newTokens.idToken),
          })
        } catch {
          clearStoredTokens()
          clearAuthCookie()
          setState({ isAuthenticated: false, isLoading: false, accessToken: null, user: null })
        }
      } else {
        setAuthCookie()
        setState({
          isAuthenticated: true,
          isLoading: false,
          accessToken: tokens.accessToken,
          user: extractUserFromTokens(tokens.accessToken, tokens.idToken),
        })
      }
    }

    checkAuth()
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const result = await cognitoSignIn(email, password)

      if ('challengeName' in result) {
        setNewPasswordChallenge({ email, session: result.session })
        return { success: false, needsNewPassword: true }
      }

      saveTokens(result)
      setAuthCookie()
      setState({
        isAuthenticated: true,
        isLoading: false,
        accessToken: result.accessToken,
        user: extractUserFromTokens(result.accessToken, result.idToken),
      })

      return { success: true }
    } catch (err) {
      const error = err as CognitoError
      let message = error.message || 'Sign in failed'

      if (error.code === 'NotAuthorizedException') {
        message = 'Incorrect email or password'
      } else if (error.code === 'UserNotFoundException') {
        message = 'Incorrect email or password'
      } else if (error.code === 'UserNotConfirmedException') {
        message = 'Please verify your email first'
      }

      return { success: false, error: message }
    }
  }, [])

  const setNewPassword = useCallback(async (newPassword: string) => {
    if (!newPasswordChallenge) {
      return { success: false, error: 'No password challenge active' }
    }

    try {
      const tokens = await respondToNewPasswordChallenge(
        newPasswordChallenge.email,
        newPassword,
        newPasswordChallenge.session
      )

      saveTokens(tokens)
      setAuthCookie()
      setNewPasswordChallenge(null)
      setState({
        isAuthenticated: true,
        isLoading: false,
        accessToken: tokens.accessToken,
        user: extractUserFromTokens(tokens.accessToken, tokens.idToken),
      })

      return { success: true }
    } catch (err) {
      const error = err as CognitoError
      let message = error.message || 'Failed to set new password'

      if (error.code === 'InvalidPasswordException') {
        message = 'Password must be at least 8 characters with letters and numbers'
      }

      return { success: false, error: message }
    }
  }, [newPasswordChallenge])

  const signOut = useCallback(async () => {
    const tokens = getStoredTokens()

    if (tokens) {
      try {
        await cognitoSignOut(tokens.accessToken)
      } catch {
        // Ignore errors during sign out
      }
    }

    clearStoredTokens()
    clearAuthCookie()
    setState({ isAuthenticated: false, isLoading: false, accessToken: null, user: null })
  }, [])

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const tokens = getStoredTokens()
    if (!tokens) return null

    if (isTokenExpired(tokens.expiresAt)) {
      try {
        const newTokens = await refreshTokens(tokens.refreshToken)
        const fullTokens: AuthTokens = {
          ...newTokens,
          refreshToken: tokens.refreshToken,
        }
        saveTokens(fullTokens)
        setState((prev) => ({
          ...prev,
          accessToken: newTokens.accessToken,
          user: extractUserFromTokens(newTokens.accessToken, newTokens.idToken),
        }))
        return newTokens.accessToken
      } catch {
        clearStoredTokens()
        clearAuthCookie()
        setState({ isAuthenticated: false, isLoading: false, accessToken: null, user: null })
        return null
      }
    }

    return tokens.accessToken
  }, [])

  // Register the token getter with the API client
  useEffect(() => {
    setAccessTokenGetter(getAccessToken)
  }, [getAccessToken])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        setNewPassword,
        signOut,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
